import { useEffect, useState } from "react";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";

import type { DeviceState } from "@/application/merchants/debug_context_repository";

import { OverlayRow, OverlaySection } from "./OverlaySection";

const client = generateClient<Schema>();

/** Amplify `a.json()` columns arrive parsed or as a string, depending on the read. */
function parseHistory(value: unknown): DeviceState["history"] {
  const parsed = typeof value === "string" ? safeParse(value) : value;
  return Array.isArray(parsed) ? (parsed as DeviceState["history"]) : [];
}

function safeParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/** The device facts themselves. Rendered wherever a device state is in hand. */
export function DeviceDetail({ device }: { device: DeviceState }) {
  return (
    <>
      <OverlaySection title="Current device">
        <OverlayRow label="App version">
          {device.appVersion
            ? `${device.appVersion}${device.buildNumber ? ` (${device.buildNumber})` : ""}`
            : null}
        </OverlayRow>
        <OverlayRow label="Platform">{device.platform}</OverlayRow>
        <OverlayRow label="Device">{device.deviceModel}</OverlayRow>
        <OverlayRow label="OS">
          {device.osName
            ? `${device.osName} ${device.osVersion ?? ""}`.trim()
            : null}
        </OverlayRow>
        <OverlayRow label="Last seen">
          {device.lastSeenAt
            ? new Date(device.lastSeenAt).toLocaleString()
            : null}
        </OverlayRow>
      </OverlaySection>

      <OverlaySection title="Version history">
        {device.history.length === 0 ? (
          <p className="text-muted-foreground text-sm">No changes recorded.</p>
        ) : (
          <ol>
            {device.history.map((entry) => (
              <li key={entry.occurredAt}>
                <OverlayRow
                  label={new Date(entry.occurredAt).toLocaleDateString()}
                >
                  {[entry.appVersion, entry.deviceModel]
                    .filter(Boolean)
                    .join(" · ") || null}
                </OverlayRow>
              </li>
            ))}
          </ol>
        )}
      </OverlaySection>
    </>
  );
}

/**
 * The device card, fetching its own snapshot.
 *
 * Reads the model directly rather than the debug-context query: this needs
 * one row, and that query fans out to six sources plus Cognito and
 * Amplitude before returning it.
 */
export function DeviceCard({ businessId }: { businessId: string }) {
  const [device, setDevice] = useState<DeviceState | null>(null);
  const [status, setStatus] = useState<"loading" | "loaded" | "error">(
    "loading",
  );

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");

    client.models.MerchantDeviceSnapshot.get({ businessId })
      .then((result) => {
        if (cancelled) return;
        if (result.errors?.length) {
          setStatus("error");
          return;
        }
        const row = result.data;
        setDevice(
          row
            ? {
                lastSeenAt: row.lastSeenAt ?? null,
                appVersion: row.appVersion ?? null,
                buildNumber: row.buildNumber ?? null,
                platform: row.platform ?? null,
                deviceModel: row.deviceModel ?? null,
                osName: row.osName ?? null,
                osVersion: row.osVersion ?? null,
                history: parseHistory(row.history),
              }
            : null,
        );
        setStatus("loaded");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [businessId]);

  if (status === "loading") {
    return <p className="text-subtle-foreground px-6 py-5 text-sm">Loading…</p>;
  }
  if (status === "error") {
    return (
      <p className="px-6 py-5 text-sm text-amber-700">
        Could not read the device snapshot.
      </p>
    );
  }
  if (!device) {
    return (
      <p className="text-muted-foreground px-6 py-5 text-sm">
        No heartbeat yet — this merchant has not opened a build that reports
        device facts.
      </p>
    );
  }

  return <DeviceDetail device={device} />;
}
